import type { FortuneSheet } from '../SheetEditor';

/**
 * What an editable drawer (SheetEditorDrawer / DocxDrawer) hands back with
 * "Add to Workspace" once the user has changed the file on screen. The drawer
 * passes `undefined` instead when nothing was edited, and the host then
 * snapshots the ORIGINAL bytes exactly as before — so an unedited add keeps
 * today's fidelity, and an edited add saves what the user actually sees.
 */
export type EditedWorkspaceSnapshot =
    | {
          kind: 'sheet';
          /** RUNTIME sheets straight from the live workbook (2D `data`, via
           *  `getAllSheets()` + `getFullSheets`) — serialized in the worker. */
          sheets: FortuneSheet[];
      }
    | {
          kind: 'document';
          /** The live Plate value (the editor's current `children`). */
          value: any[];
      };

/** Narrows an Add-to-Workspace argument to a real edited snapshot: like
 *  `isReadOnlyAddBand`, a host's `onAddToReports` can also be wired to a plain
 *  click handler, which would pass a MouseEvent. Dependency-free so host tests
 *  that mock `addFileToReports` still get the real check. */
export const isEditedWorkspaceSnapshot = (value: unknown): value is EditedWorkspaceSnapshot => {
    if (!(value instanceof Object)) return false;
    const snapshot = value as { kind?: unknown; sheets?: unknown; value?: unknown };
    return (snapshot.kind === 'sheet' && Array.isArray(snapshot.sheets)) || (snapshot.kind === 'document' && Array.isArray(snapshot.value));
};
